import { useQuery } from "@tanstack/react-query";
import { api, type OverviewData } from "../lib/api";
import {
  FacetBars,
  GENERAL_TAG_LABELS,
  SENTIMENT_LABELS,
} from "../components/FacetBars";
import { Panel } from "../components/salience/Panel";
import {
  HowItWorksCallout,
  SentimentCallout,
  IntentCallout,
  TopicsCallout,
  PrototypeNote,
} from "../components/youtube/explainers";

/**
 * Phase 5b — channel snapshot (Phase 8 #9.3 — v8 multi-facet).
 * Re-skinned 2026-06-12 onto the salience "terminal" design system so the two
 * modules read as one portal: light page header, dark stat tiles + Panels.
 * Blocks (declining importance):
 *   1. Channel state tiles (videos / comments / supportive share / views)
 *   2. v8 distributions: sentiment (up/down coloured) + general-tag + topics
 *   3. Inventory shape (transcript-status + backoff-tier counts, small print)
 *
 * Reads /api/youtube/overview (precomputed by run_youtube_ingest hourly). All
 * charts are read-only here (filtering lives on the Comments dashboard).
 */

const TOP_TOPICS_LIMIT = 8;

const TRANSCRIPT_STATUS_LABELS: Record<string, string> = {
  complete: "Complete",
  pending: "Pending",
  failed: "Failed",
  skipped: "Skipped",
  unknown: "Unknown",
};

const BACKOFF_TIER_LABELS: Record<string, string> = {
  "0": "Daily",
  "1": "Every few days",
  "2": "Weekly",
  "3": "Monthly",
};

export function Overview() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["overview"],
    queryFn: api.overview,
    retry: (failureCount, err) => {
      // 404 = "overview not yet computed" — empty state, not a transient error
      if (err instanceof Error && err.message.includes("404")) return false;
      return failureCount < 1;
    },
  });

  if (isLoading) return <OverviewSkeleton />;

  if (error) {
    const is404 = error instanceof Error && error.message.includes("404");
    return (
      <EmptyOrError
        title={is404 ? "No channel snapshot yet" : "Couldn't load the snapshot"}
        body={
          is404
            ? "We build this automatically from the collected comments. It'll appear on the next update."
            : "We couldn't load the snapshot right now. Please try again shortly."
        }
      />
    );
  }

  if (!data) return null;

  return (
    <div>
      <PageHeader generatedAt={data.generated_at} />
      <ChannelState data={data} />
      <V8Distributions comments={data.comments} />
      <InventoryShape videos={data.videos} />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Page header                                                                */
/* -------------------------------------------------------------------------- */

function PageHeader({ generatedAt }: { generatedAt?: string }) {
  return (
    <header className="mb-6">
      <div className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
        YouTube audience · Agent 1
      </div>
      <h1 className="mt-1 text-2xl font-bold tracking-tight text-ink">
        Channel snapshot
      </h1>
      <p className="mt-1.5 max-w-2xl text-sm text-ink-muted">
        What the garyseconomics audience is saying and doing. We collect the
        comments from Gary's videos automatically, and an AI reads each one to tag
        its tone, what the person is doing, and its topic.
        {generatedAt && (
          <span className="ml-1 text-ink-muted/80">
            Updated {formatRelative(generatedAt)}.
          </span>
        )}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <HowItWorksCallout tone="light" />
      </div>
      <div className="mt-2.5 max-w-2xl rounded-lg border border-rule bg-black/2 px-3 py-2">
        <PrototypeNote tone="light" variant="inline" />
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/* Block 1 — channel state tiles                                              */
/* -------------------------------------------------------------------------- */

function ChannelState({ data }: { data: OverviewData }) {
  const { videos, comments, engagement, pipeline } = data;
  const sentimentTotal = sumCounts(comments.by_sentiment);
  const supportivePct =
    sentimentTotal > 0
      ? (100 * (comments.by_sentiment["supportive"] ?? 0)) / sentimentTotal
      : null;

  return (
    <section className="mb-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          value={formatNumber(comments.total)}
          label="comments collected"
          sub={
            comments.total > 0
              ? `${comments.classified_pct}% tagged by AI`
              : undefined
          }
        />
        <StatTile
          value={supportivePct != null ? `${supportivePct.toFixed(0)}%` : "—"}
          label="supportive tone"
          sub={
            sentimentTotal > 0
              ? `of ${formatNumber(sentimentTotal)} classified`
              : undefined
          }
          accent={supportivePct != null ? "up" : undefined}
        />
        <StatTile
          value={formatNumber(engagement.total_views)}
          label="total views"
          sub={`${formatNumber(engagement.total_likes)} likes`}
        />
        <StatTile
          value={formatNumber(videos.total)}
          label="videos tracked"
          sub={
            videos.with_digest > 0
              ? `${videos.with_digest} with digest`
              : undefined
          }
        />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-white/10 bg-terminal-panel/60 px-4 py-2 text-xs text-terminal-ink-muted">
        <span>
          Comments last checked{" "}
          <span className="font-mono font-medium text-terminal-ink">
            {formatRelative(pipeline.last_comment_fetch_at)}
          </span>
        </span>
        <span aria-hidden className="text-terminal-ink-muted/40">
          ·
        </span>
        <span>
          Summaries last written{" "}
          <span className="font-mono font-medium text-terminal-ink">
            {formatRelative(pipeline.last_digest_at)}
          </span>
        </span>
      </div>
    </section>
  );
}

function StatTile({
  value,
  label,
  sub,
  accent,
}: {
  value: string;
  label: string;
  sub?: string;
  accent?: "up" | "down";
}) {
  return (
    <div className="flex flex-col rounded-xl border border-white/10 bg-terminal-panel p-4">
      <div
        className={`font-mono text-2xl font-bold leading-none tabular-nums sm:text-3xl ${
          accent === "up"
            ? "text-up"
            : accent === "down"
              ? "text-down"
              : "text-terminal-ink"
        }`}
      >
        {value}
      </div>
      <div className="mt-2 text-[11px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
        {label}
      </div>
      {sub && (
        <div className="mt-0.5 text-[11px] text-terminal-ink-muted/80">
          {sub}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Block 2 — v8 distributions (sentiment + general tags + top topics)         */
/* -------------------------------------------------------------------------- */

function V8Distributions({
  comments,
}: {
  comments: OverviewData["comments"];
}) {
  const sentimentTotal = sumCounts(comments.by_sentiment);
  const generalTotal = sumCounts(comments.by_general_tag);
  const topicTotal = sumCounts(comments.by_topic_tag);

  return (
    <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
      <Panel
        title="Audience sentiment"
        eyebrow="Tone"
        hint={
          sentimentTotal > 0
            ? `Tone toward Gary across ${formatNumber(sentimentTotal)} tagged comments.`
            : undefined
        }
        info={<SentimentCallout tone="dark" />}
      >
        <FacetBars
          counts={comments.by_sentiment}
          labelMap={SENTIMENT_LABELS}
          variant="sentiment"
          emptyMessage="No tone tagged yet. This fills in as the AI works through the comments."
        />
      </Panel>

      <Panel
        title="What the audience is doing"
        eyebrow="Intent"
        hint={
          generalTotal > 0
            ? "Questions, pushback, stories and content requests. A comment can do several, so counts overlap."
            : undefined
        }
        info={<IntentCallout tone="dark" />}
      >
        <FacetBars
          counts={comments.by_general_tag}
          labelMap={GENERAL_TAG_LABELS}
          emptyMessage="No tags yet. This fills in as the AI works through the comments."
        />
      </Panel>

      {topicTotal > 0 && (
        <Panel
          title="Top topics"
          eyebrow="Curated themes"
          hint="Most comments have no topic. These are only the ones clearly about a tracked theme."
          info={<TopicsCallout tone="dark" />}
          className="lg:col-span-2"
        >
          <FacetBars
            counts={comments.by_topic_tag}
            labelMap={comments.topic_labels}
            maxRows={TOP_TOPICS_LIMIT}
          />
        </Panel>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Block 3 — inventory shape                                                  */
/* -------------------------------------------------------------------------- */

function InventoryShape({ videos }: { videos: OverviewData["videos"] }) {
  return (
    <Panel
      title="Collection status"
      eyebrow="Behind the scenes"
      hint="A look under the hood at how our copy of the data is doing: how many videos have a transcript, and how often we re-check each one for new comments (busy videos are checked more often than quiet ones)."
    >
      <div className="grid grid-cols-1 gap-6 text-sm sm:grid-cols-2 sm:gap-8">
        <CountList
          heading="Transcripts"
          items={Object.entries(videos.by_transcript_status)
            .map(
              ([k, v]) => [TRANSCRIPT_STATUS_LABELS[k] ?? k, v] as const,
            )
            .sort((a, b) => b[1] - a[1])}
        />
        <CountList
          heading="How often we re-check for comments"
          items={Object.entries(videos.by_backoff_tier)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, v]) => [BACKOFF_TIER_LABELS[k] ?? k, v] as const)}
        />
      </div>
      {videos.livestreams > 0 && (
        <div className="mt-4 text-xs text-terminal-ink-muted">
          {videos.livestreams} livestream{videos.livestreams === 1 ? "" : "s"} are not scanned for comments.
          {videos.never_fetched > 0 &&
            ` ${videos.never_fetched} video${videos.never_fetched === 1 ? "" : "s"} not checked yet.`}
        </div>
      )}
    </Panel>
  );
}

function CountList({
  heading,
  items,
}: {
  heading: string;
  items: ReadonlyArray<readonly [string, number]>;
}) {
  return (
    <div>
      <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-terminal-ink-muted">
        {heading}
      </div>
      <ul className="space-y-1">
        {items.map(([label, count]) => (
          <li
            key={label}
            className="flex items-baseline justify-between gap-3"
          >
            <span className="text-terminal-ink-muted">{label}</span>
            <span className="font-mono text-xs tabular-nums text-terminal-ink">
              {formatNumber(count)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

function sumCounts(counts: Record<string, number>): number {
  return Object.values(counts).reduce((sum, n) => sum + n, 0);
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(0)}K`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString("en-GB");
}

function formatRelative(iso: string): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "—";
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / (1000 * 60));
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "2-digit",
  });
}

/* -------------------------------------------------------------------------- */
/* Loading + empty states                                                     */
/* -------------------------------------------------------------------------- */

function OverviewSkeleton() {
  return (
    <div>
      <div className="mb-6">
        <div className="h-3 w-40 animate-pulse rounded bg-black/5" />
        <div className="mt-2 h-7 w-64 animate-pulse rounded bg-black/5" />
      </div>
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-24 animate-pulse rounded-xl bg-terminal-panel/50"
          />
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {[0, 1].map((i) => (
          <div
            key={i}
            className="h-56 animate-pulse rounded-xl bg-terminal-panel/50"
          />
        ))}
      </div>
    </div>
  );
}

function EmptyOrError({ title, body }: { title: string; body: string }) {
  return (
    <div>
      <PageHeader />
      <Panel title={title}>
        <p className="text-sm text-terminal-ink-muted">{body}</p>
      </Panel>
    </div>
  );
}
